import { Controller } from '@hotwired/stimulus'

// Stimulus controller used for rendering alerts in the portal that were built with the full email editor.
// Shares a lot of functionality with the email-template-builder controller, but with some changes to support responsive sizing.
export default class extends Controller {
  initialize() {
    // Workaround for old alerts that have data-controller="full-email-alert" set on the iframe itself.
    // The correct behavior is to initialize the Stimulus controller on the <div id="#alert-container"> element.
    if (this.element.tagName === 'IFRAME') {
      this.element.removeAttribute('data-action')
      this.element.removeAttribute('data-controller')
      return
    }

    // Mobile UX: Alert content is in a shadow DOM
    let frames = (this.element.shadowRoot ?? this.element).querySelectorAll(
      'iframe.full-email-container',
    )
    if (frames.length === 0) return

    for (const frame of frames) this.setupFrame(frame)
    window.addEventListener('resize', () => {
      for (const frame of frames) this.adjustFrameHeight(frame)
    })
  }

  setupFrame(frame) {
    // Don't do anything if this method is called before the iframe has finished loading. Wait for the load event to fire
    if (!frame.contentDocument.body.innerHTML) {
      frame.addEventListener('load', () => this.setupFrame(frame))
      return
    }

    let html = frame.contentDocument.documentElement

    // We need to override every place we specify the width of 600 px
    for (let elem of html.querySelectorAll('[width="600"]')) {
      elem.removeAttribute('width')
      elem.style.width = 'min(100%, 600px)'
    }
    // This is only an issue for Cloudinary images. If the width or height property is not set,
    // then the image won't be scaled down when we set the `zoom` property.
    for (let img of html.querySelectorAll('img:not([width]):not([height])')) {
      if (img.height) {
        img.height = img.height
      } else {
        // Avoids setting img.height = 0
        img.addEventListener('load', () => {
          img.height = img.height
          this.adjustFrameHeight(frame)
        })
      }
    }

    // Open links in the top context to avoid CORS issues
    for (const link of frame.contentDocument.getElementsByTagName('a')) {
      link.target = '_blank'
      link.addEventListener('click', (event) => {
        event.preventDefault()
        window.open(event.currentTarget.href)
      })
    }

    this.adjustFrameHeight(frame)
  }

  adjustFrameHeight(frame) {
    let html = frame.contentDocument.documentElement,
      emailBody = frame.contentDocument.body,
      targetWidth = frame.getBoundingClientRect().width
    // NOTE: These styles are based on the *actual* look of several test emails that I opened in
    // the Gmail app on a Google Pixel 7. I'm pretty sure iOS does a similar thing based off of
    // the previews in Email On Acid.
    // If you need to make changes to this, make sure to test on a *real* mobile email client!
    html.style.zoom = null
    emailBody.style.minWidth = `${targetWidth}px !important`
    emailBody.style.width = 'min-content !important'

    const zoom = targetWidth / html.scrollWidth
    html.style.zoom = zoom
    emailBody.style.margin = 'auto'

    // We want the top-level .admin-content div to scroll, NOT the iframe
    html.style.overflow = 'hidden'
    let contentHeight = html.getBoundingClientRect().height
    // Hack for Safari to compute the height correctly
    if (navigator.vendor === 'Apple Computer, Inc.') contentHeight *= zoom
    frame.style.height = `${contentHeight}px`
  }
}
